import React, { useState, useEffect } from 'react';

// Order Timer Component - Shows countdown from order placement time
export function OrderTimer({ createdAt, status = "PENDING" }) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  
  useEffect(() => {
    if (!createdAt || status === "DELIVERED" || status === "CANCELLED") {
      setTimeLeft(null);
      return;
    }

    // Always use 10 hours for order processing time as per business requirements
    const timerHours = 10;

    const calculateTimeLeft = () => {
      // Simple and reliable date parsing for India timezone
      let orderTime;
      
      try {
        if (typeof createdAt === 'string') {
          // Handle MySQL datetime: YYYY-MM-DD HH:MM:SS
          // Convert to India time by adding +05:30
          const cleanDate = createdAt.replace(' ', 'T');
          orderTime = new Date(cleanDate + '+05:30');
          
          // If still invalid, try without timezone
          if (isNaN(orderTime.getTime())) {
            orderTime = new Date(cleanDate);
          }
        } else {
          orderTime = new Date(createdAt);
        }
        
        if (isNaN(orderTime.getTime())) {
          console.error("Invalid order time:", createdAt);
          return;
        }
      } catch (error) {
        console.error("Date parsing error:", error);
        return;
      }
      
      // Get current India time
      const now = new Date();
      const indiaOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in ms
      const localOffset = now.getTimezoneOffset() * 60 * 1000; // Local offset in ms
      const indiaTime = new Date(now.getTime() + localOffset + indiaOffset);
      
      // Calculate time difference in milliseconds
      const timeDiff = indiaTime - orderTime;
      
      // DEBUG: Log timing details
      console.log("=== TIMER DEBUG ===");
      console.log("Order Created At (raw):", createdAt);
      console.log("Order Time (parsed):", orderTime.toString());
      console.log("Current System Time:", now.toString());
      console.log("Current India Time:", indiaTime.toString());
      console.log("Time Difference (ms):", timeDiff);
      console.log("Time Difference (hours):", timeDiff / (1000 * 60 * 60));
      
      // Use configurable hours = timerHours * 60 * 60 * 1000 milliseconds
      const totalTime = timerHours * 60 * 60 * 1000;
      const remaining = totalTime - timeDiff;
      
      console.log("Total Time (" + timerHours + " hours):", totalTime);
      console.log("Remaining Time (ms):", remaining);
      console.log("Remaining Time (hours):", remaining / (1000 * 60 * 60));
      console.log("==================");
      
      if (remaining <= 0) {
        setIsExpired(true);
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
      
      setTimeLeft({ hours, minutes, seconds });
      setIsExpired(false);
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [createdAt, status]);

  if (!timeLeft) return null;

  const getTimerColor = () => {
    if (isExpired) return { color: '#dc2626', background: '#fef2f2', border: '#fecaca' };
    if (timeLeft.hours <= 2) return { color: '#d97706', background: '#fffbeb', border: '#fde68a' };
    if (timeLeft.hours <= 5) return { color: '#4f46e5', background: '#eff6ff', border: '#bfdbfe' };
    return { color: '#16a34a', background: '#f0fdf4', border: '#bbf7d0' };
  };

  const timerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    border: `1px solid ${getTimerColor().border}`,
    background: getTimerColor().background,
    color: getTimerColor().color,
    fontFamily: 'ui-monospace, monospace',
  };

  const formatTime = (num) => String(num).padStart(2, '0');

  if (isExpired) {
    return (
      <div style={timerStyle}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 6v6l4 2"/>
        </svg>
        EXPIRED
      </div>
    );
  }

  return (
    <div style={timerStyle}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 6v6l4 2"/>
      </svg>
      {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
    </div>
  );
}

// Helper function to calculate if order is within processing window (always 10 hours)
export function isOrderWithinTimeWindow(createdAt) {
  if (!createdAt) return false;
  
  const orderTime = new Date(createdAt);
  const now = new Date();
  const timeDiff = now - orderTime;
  const timeWindow = 10 * 60 * 60 * 1000; // Always 10 hours
  
  return timeDiff <= timeWindow;
}

// Helper function to get time remaining in hours (always 10 hours)
export function getTimeRemaining(createdAt) {
  if (!createdAt) return 0;
  
  const orderTime = new Date(createdAt);
  const now = new Date();
  const timeDiff = now - orderTime;
  const timeWindow = 10 * 60 * 60 * 1000; // Always 10 hours
  const remaining = timeWindow - timeDiff;
  
  return Math.max(0, Math.floor(remaining / (1000 * 60 * 60)));
}
